import { ButtonType, DialogFooterButtonsWidget } from '@tableau/widgets';
import * as React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CloseAndDismiss } from '~src/commonInfra/dialogs/ReactModalService';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';

import { TestEncryptionKeyConnectionRequest, TestEncryptionKeyConnectionResult } from '../serverApi/requests/TestEncryptionKeyConnectionRequest';
import { ExtractEncryptionKeyService } from '../services/ExtractEncryptionKeyService';
import { ProgressBarBackgroundFill, ProgressBarForegroundFill } from '../styles/ExtractEncryptionSettingsStyles';



export interface KMSTestConnectionViewState {
    currentProgressPercentage: number;
    isTestingConnection: boolean;
    testConnectionResult: TestEncryptionKeyConnectionResult;
}

export interface KMSTestConnectionViewProps {
    closeDialog: CloseAndDismiss;
    externalKeyId: string;
}


async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
}

export class KMSTestConnectionView extends React.PureComponent<KMSTestConnectionViewProps, KMSTestConnectionViewState> {
    constructor(props: KMSTestConnectionViewProps) {
        super(props);
        this.state = {
            currentProgressPercentage: 0,
            isTestingConnection: true,
            testConnectionResult: null
        };
    }


    public componentDidMount(): void {
      this.setState({
        isTestingConnection: true, currentProgressPercentage: 0}, () => {
        this.testEncryptionKeyConnection();
        this.increaseProgressBar();
    });
  }


    public render(): JSX.Element {
        return <>
          <div>
            {this.state.isTestingConnection ? this.renderProgressBar() : this.renderTestConnectionResult()}
          </div>
        <div style={{ position: 'relative' , paddingTop: '20px'}}>
            {this.getFooterContent()}
        </div>
    </>;
    }



    async increaseProgressBar() {
      while(this.state.currentProgressPercentage < 100){
        const currProgress = this.state.isTestingConnection ? this.state.currentProgressPercentage + 5 : 100;
        this.setState({currentProgressPercentage: currProgress});
        await sleep(500);
      }
      this.setState({isTestingConnection: false});
    }


    private renderProgressBar() {
        const currentProgressPercentage = Math.floor(this.state.currentProgressPercentage / 100);
        return <div>
         <div style={{font: 'BentonSans Book', paddingTop:'10px', fontSize:'14px', paddingBottom: '5px', alignContent: 'center'}}>
            <div style={{float: 'left'}}>{Messages.extractEncryption_KMSKeyTestConnectionDialog_Description()}</div><div style={{float:'right'}}>{`${this.state.currentProgressPercentage}%`}</div>
          </div>
          <div
            aria-valuenow={currentProgressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            role='progressbar'
          >
            <span style={ProgressBarBackgroundFill}>
              <div style={{...ProgressBarForegroundFill,width: this.state.currentProgressPercentage + '%' }} data-tb-test-id={'SYNC_PROGRESS_BAR'}></div>
            </span>
          </div>
        </div>;
      }

    private testEncryptionKeyConnection() {
      this.setState({isTestingConnection: true}, () => {
        ExtractEncryptionKeyService.testEncryptionKeyConnection(new TestEncryptionKeyConnectionRequest({externalKeyId: this.props.externalKeyId}))
        .then((response: TestEncryptionKeyConnectionResult) => {
          this.setState({testConnectionResult: response});
        }).catch(() => {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryption_KMSKeyTestConnectionDialog_FailureToast() });
        }).finally(() => {
          this.setState({isTestingConnection: false});
        });
      });
    }

    private renderTestConnectionResult(){
        return <div>
            <div><span style={{fontWeight: 'bold'}}>{Messages.extractEncryption_KMSKeyTestConnectionDialog_UniqueKeyIdentifier()}: </span><span>{this.props.externalKeyId}</span></div>
            <br></br>
            <div>{this.getStatusMessage()}</div>
            <div>{this.getStatusMessageDescription()}</div>
        </div>;
    }

    private getStatusMessage(): string {
      if(this.state.testConnectionResult?.success){
        return Messages.extractEncryption_KMSKeyTestConnectionDialog_Success();
      }
      return `Error: ${this.state.testConnectionResult?.errorType}`;
    }

    private getStatusMessageDescription(): string {
      switch(this.state.testConnectionResult?.errorType){
        case 'KEY_NOT_FOUND':
          return Messages.extractEncryption_KMSKeyTestConnectionDialog_NotFound_Description();
        case 'POLICY_DENIED':
          return Messages.extractEncryption_KMSKeyTestConnectionDialog_PolicyDenied_Description();
        case 'UNKNOWN':
          return Messages.extractEncryption_KMSKeyTestConnectionDialog_Unknown_Description();
        default:
          return '';
      }
    }

    private getFooterContent = (): React.ReactNode => {
      return (
          <>
            <DialogFooterButtonsWidget
                buttons={[
                    {
                        handleClick: () => {
                            this.props.closeDialog.close();
                        },
                        buttonType: ButtonType.LowEmphasis,
                        children: this.state.isTestingConnection ? Messages.cancel() : Messages.close(),
                        testId: 'cancel-button',
                        style: {outline: 'none'}
                    }
                ]}
            />
          </>
      );
  };
}
